/**
 * KAN-770: object URLs of files from the authenticated API (see useApiFileSrc).
 *
 * Lists show the same file in many rows, so a file is fetched once and shared.
 * The cache is an LRU of API_FILE_CACHE_LIMIT loaded entries: the least
 * recently used loaded entry is evicted and its object URL revoked. Entries
 * still loading are never evicted (a long transcript loads all its pictures at
 * once). A failed load is dropped and retried by the next call. The cache has
 * no imports, so the auth session can clear it without an import cycle.
 */

export const API_FILE_CACHE_LIMIT = 50;

export type ApiFileFetcher = (url: string) => Promise<Blob>;

interface Entry {
  version: string;
  src: Promise<string>;
  objectUrl: string | null;
  dropped: boolean;
}

const cache = new Map<string, Entry>();

function drop(entry: Entry): void {
  entry.dropped = true;
  if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
}

function evictOverflow(): void {
  for (const [url, entry] of cache) {
    if (cache.size <= API_FILE_CACHE_LIMIT) return;
    if (entry.objectUrl === null) continue;
    cache.delete(url);
    drop(entry);
  }
}

/**
 * Object URL of a file. A known `version` that differs from the cached one
 * replaces (and revokes) the entry; '' accepts any cached version.
 */
export function loadApiFileUrl(
  url: string,
  version: string,
  fetcher: ApiFileFetcher,
): Promise<string> {
  const cached = cache.get(url);
  if (cached) {
    cache.delete(url);
    if (!version || cached.version === version) {
      cache.set(url, cached);
      return cached.src;
    }
    drop(cached);
  }
  const entry: Entry = {
    version,
    objectUrl: null,
    dropped: false,
    src: Promise.resolve(''),
  };
  // A fetcher that throws synchronously still yields a rejected promise.
  entry.src = Promise.resolve()
    .then(() => fetcher(url))
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      if (entry.dropped) {
        URL.revokeObjectURL(objectUrl);
        throw new Error('api file cache entry dropped');
      }
      entry.objectUrl = objectUrl;
      evictOverflow();
      return objectUrl;
    });
  entry.src.catch(() => {
    if (cache.get(url) === entry) cache.delete(url);
  });
  cache.set(url, entry);
  return entry.src;
}

/** Revoke and forget every file (end of the auth session: files of the previous user). */
export function clearApiFileCache(): void {
  for (const entry of cache.values()) drop(entry);
  cache.clear();
}
